import { useLazyLoad } from "~/common/media/useLazyLoad"
import { formatDateTime, optionalValue } from "~/common/utils"
import { useLayoutEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router"
import type { Dayjs } from "dayjs"
import dayjs from "dayjs"
import {
  ReloadOutlined,
  SearchOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons"
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Select,
  Table,
  Tag,
  Typography,
} from "antd"
import type { TableProps } from "antd"
import { listStaffUserVideoTasks } from "~/api/staff"
import { VIDEO_TOOL_LABELS } from "~/common/constants"
import { normalizeTemplateValues } from "~/common/videoTemplate"
import { useVideoTemplate } from "~/store/videoTemplates"
import { ResourcePreview } from "~/components/ResourcePreview"
import { usePagedQuery } from "~/pages/Staff/usePagedQuery"
import styles from "./index.css"

const { Paragraph, Text, Title } = Typography
const { RangePicker } = DatePicker
const defaultPageSize = 10
const maxPageSize = 20
const defaultTaskType: API.Staff.StaffVideoTaskType = "hot_video"
const defaultCreateVideoStatus: API.Staff.StaffCreateVideoStatus = 3
const videoLazyLoadRootMargin = "200px"

const taskTypeOptions: Array<{
  label: string
  value: API.Staff.StaffVideoTaskType
}> = [
  { label: "爆款复刻", value: "hot_video" },
  { label: "一键同款", value: "one_click_video" },
  { label: "直接生成视频", value: "create_video" },
  { label: VIDEO_TOOL_LABELS.frameCopy, value: "frame_copy" },
]

const createVideoStatusOptions: Array<{
  label: string
  value: API.Staff.StaffCreateVideoStatus
}> = [
  { label: "初始状态", value: 1 },
  { label: "生成中", value: 2 },
  { label: "生成成功", value: 3 },
  { label: "生成失败", value: 4 },
]

type DownloadStatus = "downloaded" | "notDownloaded"

const downloadStatusOptions: Array<{
  label: string
  value: DownloadStatus
}> = [
  { label: "已下载", value: "downloaded" },
  { label: "未下载", value: "notDownloaded" },
]

type VideoTaskFilterForm = {
  createdRange?: [Dayjs, Dayjs]
  createVideoStatus: API.Staff.StaffCreateVideoStatus
  downloadStatus?: DownloadStatus
  taskId?: string
  taskType: API.Staff.StaffVideoTaskType
  userId?: string
}

type VideoTaskFilters = Pick<
  API.Staff.StaffVideoTaskListParams,
  | "task_type"
  | "create_video_status"
  | "task_id"
  | "user_id"
  | "created_from"
  | "created_to"
  | "has_download"
>

const getDefaultCreatedRange = (): [Dayjs, Dayjs] => {
  const end = dayjs()
  return [end.subtract(3, "day"), end]
}

const getDefaultFilters = (createdRange: [Dayjs, Dayjs]): VideoTaskFilters => ({
  task_type: defaultTaskType,
  create_video_status: defaultCreateVideoStatus,
  created_from: createdRange[0].toISOString(),
  created_to: createdRange[1].toISOString(),
})

const getTemplateId = (value: string) => {
  try {
    return normalizeTemplateValues(JSON.parse(value))?.templateId
  } catch {
    return undefined
  }
}

type ExpandableCopyableTextProps = {
  emptyText?: string
  value: string
}

const ExpandableCopyableText = ({
  emptyText = "-",
  value,
}: ExpandableCopyableTextProps) => {
  const textRef = useRef<HTMLSpanElement | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [canExpand, setCanExpand] = useState(false)

  useLayoutEffect(() => {
    setExpanded(false)
  }, [value])

  useLayoutEffect(() => {
    const element = textRef.current

    if (!element || expanded) return

    const measureOverflow = () => {
      setCanExpand(element.scrollHeight > element.clientHeight + 1)
    }

    measureOverflow()
    const observer = new ResizeObserver(measureOverflow)
    observer.observe(element)

    return () => observer.disconnect()
  }, [expanded, value])

  if (!value) return emptyText

  return (
    <Flex
      align="flex-start"
      vertical
    >
      <Paragraph
        className={styles.textCell}
        copyable={{ text: value, tooltips: ["复制", "已复制"] }}
      >
        <span
          ref={textRef}
          className={expanded ? styles.expandedText : styles.clampedText}
        >
          {value}
        </span>
      </Paragraph>
      {canExpand ? (
        <Button
          className={styles.expandButton}
          size="small"
          type="link"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? "收起" : "展开"}
        </Button>
      ) : null}
    </Flex>
  )
}

const renderCopyableText = (value: string, emptyText = "-") => {
  return (
    <ExpandableCopyableText
      emptyText={emptyText}
      value={value}
    />
  )
}

const renderImages = (imageUrls: string[]) => {
  if (imageUrls.length === 0) return "-"

  return (
    <Flex
      gap={6}
      wrap
    >
      {imageUrls.map((url, index) => (
        <ResourcePreview
          type="image"
          alt={`参考图${index + 1}`}
          key={`${url}-${index}`}
          preview={{ mask: "预览" }}
          size="small"
          src={url}
        />
      ))}
    </Flex>
  )
}

const TemplateCover = ({ values }: { values: string }) => {
  const { template } = useVideoTemplate(getTemplateId(values))

  if (!template?.videoCover) return "-"

  return (
    <ResourcePreview
      type="image"
      alt={template.title}
      preview={{ mask: "预览" }}
      size="medium"
      src={template.videoCover}
    />
  )
}

type LazyVideoPreviewProps = {
  src: string
}

const LazyVideoPreview = ({ src }: LazyVideoPreviewProps) => {
  const { ref: containerRef, shouldLoad } = useLazyLoad(videoLazyLoadRootMargin)

  return (
    <div
      className={styles.videoPreview}
      ref={containerRef}
    >
      {shouldLoad ? (
        <ResourcePreview
          type="video"
          className={styles.videoPreviewContent}
          src={src}
        />
      ) : (
        <div
          aria-label="视频等待加载"
          className={styles.videoPlaceholder}
        >
          <VideoCameraOutlined />
        </div>
      )}
    </div>
  )
}

const renderVideo = (videoUrl?: string | null) => {
  if (!videoUrl) return "-"

  return <LazyVideoPreview src={videoUrl} />
}

const getDownloadCount = (record: API.Staff.StaffVideoTaskItem) => {
  if (record.task_type === "hot_video" || record.task_type === "frame_copy") {
    return record.create_video.download_count
  }

  return record.download_count
}

const getModel = (record: API.Staff.StaffVideoTaskItem) => {
  if (record.task_type === "hot_video" || record.task_type === "frame_copy") {
    return record.create_video.model
  }

  return record.model
}

const getResolution = (record: API.Staff.StaffVideoTaskItem) => {
  if (record.task_type === "hot_video" || record.task_type === "frame_copy") {
    return record.create_video.resolution
  }

  return record.resolution
}

const commonColumns: NonNullable<
  TableProps<API.Staff.StaffVideoTaskItem>["columns"]
> = [
  {
    title: "任务创建时间",
    dataIndex: "created_t",
    width: 180,
    render: formatDateTime,
  },
  {
    title: "用户ID",
    dataIndex: "user_id",
    width: 220,
    render: (value: string) => renderCopyableText(value),
  },
  {
    title: "是否 Staff",
    dataIndex: "is_staff",
    width: 110,
    render: (value: boolean) => (
      <Tag color={value ? "default" : "blue"}>{value ? "是" : "否"}</Tag>
    ),
  },
  {
    title: "任务ID",
    dataIndex: "task_id",
    width: 220,
    render: (value: string) => renderCopyableText(value),
  },
  {
    title: "下载次数",
    align: "center",
    width: 100,
    render: (_, record) => getDownloadCount(record) ?? 0,
  },
]

const getColumns = (
  taskType: API.Staff.StaffVideoTaskType,
): TableProps<API.Staff.StaffVideoTaskItem>["columns"] => {
  if (taskType === "hot_video" || taskType === "frame_copy") {
    return [
      ...commonColumns,
      {
        title: "原视频",
        width: 112,
        render: (_, record) => {
          switch (record.task_type) {
            case "hot_video":
              return renderVideo(record.parse_video.video_url)
            case "frame_copy":
              return renderVideo(record.split_frame.video_url)
            default:
              return "-"
          }
        },
      },
      {
        title: "参考图",
        width: 180,
        render: (_, record) => {
          if (
            record.task_type !== "hot_video" &&
            record.task_type !== "frame_copy"
          ) {
            return "-"
          }
          return renderImages(record.create_video.image_urls)
        },
      },
      {
        title: "提示词",
        width: 360,
        render: (_, record) => {
          if (
            record.task_type !== "hot_video" &&
            record.task_type !== "frame_copy"
          ) {
            return "-"
          }
          return renderCopyableText(record.create_video.prompt)
        },
      },
      {
        title: "视频生成时间",
        width: 180,
        render: (_, record) => {
          if (
            record.task_type !== "hot_video" &&
            record.task_type !== "frame_copy"
          ) {
            return "-"
          }
          return formatDateTime(record.create_video.started_at)
        },
      },
      {
        title: "模型名称",
        width: 180,
        render: (_, record) => {
          if (
            record.task_type !== "hot_video" &&
            record.task_type !== "frame_copy"
          ) {
            return "-"
          }
          return renderCopyableText(getModel(record))
        },
      },
      {
        title: "清晰度",
        width: 100,
        render: (_, record) => {
          return getResolution(record)
        },
      },
      {
        title: "最终视频",
        width: 112,
        render: (_, record) => {
          if (
            record.task_type !== "hot_video" &&
            record.task_type !== "frame_copy"
          ) {
            return "-"
          }
          return renderVideo(record.create_video.video_url)
        },
      },
    ]
  }

  if (taskType === "one_click_video") {
    return [
      ...commonColumns,
      {
        title: "参考图",
        width: 180,
        render: (_, record) =>
          record.task_type === "one_click_video"
            ? renderImages(record.image_urls)
            : "-",
      },
      {
        title: "提示词",
        width: 360,
        render: (_, record) =>
          record.task_type === "one_click_video"
            ? renderCopyableText(record.prompt)
            : "-",
      },
      {
        title: "模板封面",
        width: 112,
        render: (_, record) =>
          record.task_type === "one_click_video" ? (
            <TemplateCover values={record.template_values_str} />
          ) : (
            "-"
          ),
      },
      {
        title: "模型",
        width: 180,
        render: (_, record) =>
          record.task_type === "one_click_video"
            ? renderCopyableText(record.model)
            : "-",
      },
      {
        title: "清晰度",
        width: 100,
        render: (_, record) =>
          record.task_type === "one_click_video"
            ? record.resolution || "-"
            : "-",
      },
      {
        title: "最终视频",
        width: 112,
        render: (_, record) =>
          record.task_type === "one_click_video"
            ? renderVideo(record.video_url)
            : "-",
      },
    ]
  }

  return [
    ...commonColumns,
    {
      title: "提示词",
      width: 480,
      render: (_, record) =>
        record.task_type === "create_video"
          ? renderCopyableText(record.prompt)
          : "-",
    },
    {
      title: "模型",
      width: 180,
      render: (_, record) =>
        record.task_type === "create_video"
          ? renderCopyableText(record.model)
          : "-",
    },
    {
      title: "清晰度",
      width: 100,
      render: (_, record) =>
        record.task_type === "create_video" ? record.resolution || "-" : "-",
    },
    {
      title: "生成视频",
      width: 112,
      render: (_, record) =>
        record.task_type === "create_video"
          ? renderVideo(record.video_url)
          : "-",
    },
  ]
}

export const StaffUserVideoList = () => {
  const [params] = useSearchParams()
  const [initialTaskId] = useState(
    () => params.get("taskId")?.trim() || undefined,
  )
  const [initialStatus] = useState<API.Staff.StaffCreateVideoStatus>(() => {
    const status = Number(params.get("status"))
    return status === 1 || status === 2 || status === 3 || status === 4
      ? status
      : defaultCreateVideoStatus
  })
  const [form] = Form.useForm<VideoTaskFilterForm>()
  const [initialCreatedRange] = useState(getDefaultCreatedRange)
  const { filters, total, loading, search, refresh, tableProps } =
    usePagedQuery<VideoTaskFilters, API.Staff.StaffVideoTaskItem>({
      fetchPage: listStaffUserVideoTasks,
      initialFilters: {
        ...getDefaultFilters(initialCreatedRange),
        task_id: initialTaskId,
        create_video_status: initialStatus,
        ...(initialTaskId
          ? { created_from: undefined, created_to: undefined }
          : {}),
      },
      defaultPageSize,
      pageSizeOptions: [defaultPageSize, maxPageSize],
      errorMessage: "用户视频任务加载失败，请稍后重试",
    })
  const columns = useMemo(
    () => getColumns(filters.task_type),
    [filters.task_type],
  )

  const applyFilters = async () => {
    const values = await form.validateFields()
    const createdRange = values.createdRange

    search({
      task_type: values.taskType,
      create_video_status: values.createVideoStatus,
      task_id: optionalValue(values.taskId),
      user_id: optionalValue(values.userId),
      created_from: createdRange?.[0].toISOString(),
      created_to: createdRange?.[1].toISOString(),
      has_download:
        values.downloadStatus === undefined
          ? undefined
          : values.downloadStatus === "downloaded",
    })
  }

  const resetFilters = () => {
    const createdRange = getDefaultCreatedRange()

    form.setFieldsValue({
      createdRange,
      createVideoStatus: defaultCreateVideoStatus,
      downloadStatus: undefined,
      taskId: undefined,
      taskType: defaultTaskType,
      userId: undefined,
    })
    search(getDefaultFilters(createdRange))
  }

  return (
    <Flex
      className={styles.page}
      vertical
      gap={18}
    >
      <Flex
        vertical
        gap={6}
      >
        <Title level={2}>用户制作视频列表</Title>
        <Text type="secondary">
          按用户ID、制作方式与任务创建时间查看用户提交的视频任务和生成内容。
        </Text>
      </Flex>

      <section className={styles.filterPanel}>
        <Form<VideoTaskFilterForm>
          form={form}
          initialValues={{
            createdRange: initialTaskId ? undefined : initialCreatedRange,
            createVideoStatus: initialStatus,
            taskId: initialTaskId,
            taskType: defaultTaskType,
          }}
          layout="vertical"
          onFinish={applyFilters}
        >
          <Flex
            align="flex-end"
            gap={16}
            wrap
          >
            <Form.Item
              className={styles.taskTypeField}
              label="任务类型"
              name="taskType"
              rules={[{ required: true, message: "请选择任务类型" }]}
            >
              <Select options={taskTypeOptions} />
            </Form.Item>
            <Form.Item
              className={styles.statusField}
              label="视频生成状态"
              name="createVideoStatus"
              rules={[{ required: true, message: "请选择视频生成状态" }]}
            >
              <Select options={createVideoStatusOptions} />
            </Form.Item>
            <Form.Item
              className={styles.downloadStatusField}
              label="下载状态"
              name="downloadStatus"
            >
              <Select
                allowClear
                options={downloadStatusOptions}
                placeholder="不限"
              />
            </Form.Item>
            <Form.Item
              className={styles.taskIdField}
              label="任务ID"
              name="taskId"
            >
              <Input
                allowClear
                placeholder="请输入任务ID"
              />
            </Form.Item>
            <Form.Item
              className={styles.userIdField}
              label="用户ID"
              name="userId"
            >
              <Input
                allowClear
                placeholder="请输入用户ID"
              />
            </Form.Item>
            <Form.Item
              className={styles.dateRangeField}
              label="任务创建时间"
              name="createdRange"
            >
              <RangePicker
                allowClear
                format="YYYY-MM-DD HH:mm"
                placeholder={["开始时间", "结束时间"]}
                showTime={{ format: "HH:mm" }}
              />
            </Form.Item>
            <Form.Item className={styles.actionField}>
              <Flex gap={10}>
                <Button onClick={resetFilters}>重置</Button>
                <Button
                  htmlType="submit"
                  icon={<SearchOutlined />}
                  loading={loading}
                  type="primary"
                >
                  查询
                </Button>
              </Flex>
            </Form.Item>
          </Flex>
        </Form>
      </section>

      <section className={styles.listPanel}>
        <Flex
          className={styles.listHeader}
          align="center"
          justify="space-between"
        >
          <Flex
            align="center"
            gap={8}
          >
            <VideoCameraOutlined className={styles.sectionIcon} />
            <Title level={4}>视频任务记录</Title>
            <Text type="secondary">共 {total} 条</Text>
          </Flex>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => refresh()}
          >
            刷新
          </Button>
        </Flex>
        <Table
          columns={columns}
          {...tableProps}
          rowKey="task_id"
          scroll={{ x: "max-content" }}
        />
      </section>
    </Flex>
  )
}
